/**
 * Pure helpers for the Plan 16 /orders/kitchen page. Lives in its
 * own module so unit tests can run without booting the React tree
 * (the page itself reaches for TanStack Query + the router + a
 * dozen `@/api` and `@/components` aliases the bun-test runner
 * doesn't resolve outside the Vite build).
 *
 * Same shape as `apps/ordo/client/src/pages/recipes/helpers.ts`.
 */

import type { KitchenView } from '../../components/orders/kitchen/view-toggle'
import type { Campus, Deliverable } from '../../lib/chefs/types'

/**
 * Gate for the chef kitchen surfaces (/orders/kitchen, /shopping-list):
 * what to render given the state of the my-kitchens query.
 *
 *   - HOLD  — kitchens still resolving; keep the skeleton up.
 *   - ERROR — the my-kitchens fetch failed; callers currently keep their
 *             pre-existing behavior for this (scoped out of Plan follow-up).
 *   - EMPTY — resolved successfully to ZERO kitchens. Render an explicit
 *             "No kitchen memberships" empty state instead of the silent
 *             empty "My Kitchen" schedule it used to degrade into.
 *   - READY — at least one kitchen; render the page.
 */
export type KitchenGate = 'HOLD' | 'ERROR' | 'EMPTY' | 'READY'

export function resolveKitchenGate(args: { pending: boolean; errored: boolean; kitchenCount: number }): KitchenGate {
  if (args.pending) {
    return 'HOLD'
  }
  if (args.errored) {
    return 'ERROR'
  }
  if (args.kitchenCount === 0) {
    return 'EMPTY'
  }
  return 'READY'
}

/**
 * Read `?view=day|week` off the URL search params. Falls back to
 * `'day'` — chefs care about today first; the week view is the
 * explicit "plan ahead" jump.
 */
export function parseView(raw: string | null): KitchenView {
  if (raw === 'week' || raw === 'day') {
    return raw
  }
  return 'day'
}

/**
 * Read `?date=YYYY-MM-DD` off the URL search params. Falls back to
 * today (UTC). Garbage / invalid dates also fall back to today
 * rather than throwing — share-link UX > strict input validation.
 */
export function parseDateOrToday(raw: string | null): Date {
  if (!raw) {
    return startOfTodayUtc()
  }
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) {
    return startOfTodayUtc()
  }
  const dt = new Date(Date.UTC(Number(m[1]!), Number(m[2]!) - 1, Number(m[3]!)))
  if (!Number.isFinite(dt.getTime())) {
    return startOfTodayUtc()
  }
  return dt
}

function startOfTodayUtc(): Date {
  const now = new Date()
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
}

/**
 * The selected row, re-found by id across every campus on the page (the page
 * keeps the ID, not the object, so the selection survives the 30s refetch).
 * First match wins, which is only correct because `shipmentsToCampuses`
 * namespaces every row id by its campus (`deliverableRowId`) — with the bare
 * row key, a 39-meal campus's row resolved to a sibling school's identical
 * 355x package (PROD-1341).
 */
export function findDeliverableById(campuses: ReadonlyArray<Campus>, id: string): Deliverable | null {
  for (const c of campuses) {
    for (const b of c.mealtimes) {
      const hit = b.deliverables.find((d) => d.id === id)
      if (hit) {
        return hit
      }
    }
  }
  return null
}

/**
 * Chef order for the /orders/kitchen campus list: Organization >
 * Locations, both descending alphabetically. Organization is the
 * primary key so every campus of one organization stays contiguous —
 * the campus name only breaks ties inside an organization.
 *
 * Page-local on purpose: `shipmentsToCampuses` keeps its
 * campus-name-ascending order for /shopping-list, which is
 * unaffected by this view's grouping. Both /orders/kitchen surfaces
 * — the on-page campus list and the printed packet's per-day arrays
 * — go through here, so the packet a chef prints matches the screen
 * they printed it from. Returns a new array rather than sorting in
 * place.
 */
export function sortCampusesForOrders<T extends { organizationName: string; name: string }>(
  campuses: readonly T[],
): T[] {
  return [...campuses].sort((a, b) => {
    const byOrganization = b.organizationName.localeCompare(a.organizationName)
    if (byOrganization !== 0) {
      return byOrganization
    }
    return b.name.localeCompare(a.name)
  })
}
